import ReactGA from 'react-ga4';

/**
 * Google Analytics Service
 * Wrapper around react-ga4 for consistent event tracking
 */
class GoogleAnalyticsService {
  private initialized = false;
  private userId: string | null = null;

  /**
   * Initialize Google Analytics
   */
  init() {
    if (this.initialized) return;

    const gaId = process.env.REACT_APP_GA_MEASUREMENT_ID;

    if (!gaId) {
      console.warn('Google Analytics Measurement ID not configured. GA will be disabled.');
      return;
    }

    try {
      ReactGA.initialize(gaId, {
        gaOptions: {
          siteSpeedSampleRate: 100,
        },
        gtagOptions: {
          send_page_view: false, // We'll track pageviews manually
        },
      });

      this.initialized = true;

      if (process.env.NODE_ENV === 'development') {
        console.log('Google Analytics initialized with ID:', gaId);
      }
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
    }
  }

  /**
   * Identify a user
   */
  identify(userId: string, properties?: Record<string, any>) {
    if (!this.initialized) return;

    this.userId = userId;

    ReactGA.set({ userId });

    if (properties) {
      ReactGA.set(properties);
    }
  }

  /**
   * Reset user identification (on logout)
   */
  reset() {
    if (!this.initialized) return;

    this.userId = null;
    ReactGA.set({ userId: null });
  }

  /**
   * Track an event
   */
  track(eventName: string, properties?: Record<string, any>) {
    if (!this.initialized) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[GA]', eventName, properties);
      }
      return;
    }

    try {
      ReactGA.event({
        category: properties?.category || 'General',
        action: eventName,
        label: properties?.label,
        value: properties?.value,
        ...properties,
      });
    } catch (error) {
      console.error('Error tracking GA event:', error);
    }
  }

  /**
   * Track page view
   */
  trackPageView(path: string, title?: string) {
    if (!this.initialized) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[GA] Page View:', path, title);
      }
      return;
    }

    try {
      ReactGA.send({
        hitType: 'pageview',
        page: path,
        title: title || document.title,
      });
    } catch (error) {
      console.error('Error tracking GA page view:', error);
    }
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: Record<string, any>) {
    if (!this.initialized || !this.userId) return;

    try {
      ReactGA.set(properties);
    } catch (error) {
      console.error('Error setting GA user properties:', error);
    }
  }

  /**
   * Track conversion funnel step
   */
  trackFunnelStep(funnelName: string, step: string, properties?: Record<string, any>) {
    this.track('funnel_step', {
      category: 'Funnel',
      label: `${funnelName} - ${step}`,
      funnel_name: funnelName,
      step,
      ...properties,
    });
  }

  /**
   * Check if analytics is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}

export const googleAnalytics = new GoogleAnalyticsService();
export default googleAnalytics;
